<script setup lang="ts">
import { useDisplay } from 'vuetify'
import { VIconBtn } from 'vuetify/labs/VIconBtn'
import SearchHighlightText from '@/components/createdUi/SearchHighlightText.vue'
import UniversalFilterTree from '@/components/createdUi/UniversalFilterTree.vue'
import { getFullName } from '@/utils/formatters'
import { type IFilterConfig, useTableFilters } from '@/composables/useTableFilters'
import { useTableItemsPerPage } from '@/composables/useTableItemsPerPage'
import { refDebounced } from '@vueuse/core'
import { useStaffSettingsStore } from '../../stores/staffSettingsStore'

const props = withDefaults(
  defineProps<{
    items: Array<{
      id: string
      last_name?: string
      first_name?: string
      middle_name?: string
      position_name?: string | null
      department_name?: string | null
      phone_number?: string | null
      email?: string | null
    }>
    total: number
    nextCursor: string | null
    headers: Array<{
      title: string
      key: string
      sortable?: boolean
      filterType?: string
      filterKey?: string
      width?: number | string
    }>
    fetchPage: (
      search: string,
      cursor?: string,
      limit?: number,
      sort?: string | null,
      filters?: Record<string, any>,
    ) => Promise<void>
    reloadKey?: string | null
    dragEnabled?: boolean
    moveLabel: string
    moveTooltip?: string
    addLabel: string
    emptyText: string
    storageKey: string
    canAdd?: boolean
    /** Клик по строке эмитит edit; включается родителем при наличии прав на редактирование. */
    rowEditable?: boolean
    filterStore?: any
    sortMap?: Record<string, string>
    filterConfigs?: IFilterConfig[]
  }>(),
  {
    canAdd: true,
    rowEditable: false,
    dragEnabled: false,
    reloadKey: null,
    moveTooltip: '',
    sortMap: undefined,
    filterConfigs: undefined,
    filterStore: undefined,
  },
)

const emit = defineEmits<{
  move: []
  add: []
  edit: [id: string]
}>()

const { xs } = useDisplay()
const selectedIds = defineModel<string[]>('selectedIds', { default: () => [] })

const staffSettingsStore = useStaffSettingsStore()
const activeFilterStore = computed(() => props.filterStore || staffSettingsStore)

const defaultSortMap: Record<string, string> = {
  fio: 'full_name',
  name: 'full_name',
  position_name: 'position_name',
  department_name: 'department_name',
  phone_number: 'phone_number',
  email: 'email',
}
const effectiveSortMap = computed(() => props.sortMap || defaultSortMap)

const defaultFilterConfigs: IFilterConfig[] = [
  { key: 'positions', labelPrefix: 'Должность: ', type: 'tree' },
  { key: 'departments', labelPrefix: 'Подразделение: ', type: 'tree' },
]
const effectiveFilterConfigs = computed(() => props.filterConfigs || defaultFilterConfigs)

const search = ref('')
const searchDebounced = refDebounced(search, 300)
/** Строка, с которой реально ушёл запрос. Не ждёт debounce при смене сущности. */
const appliedSearch = ref('')
const ignoreNextSearchFetch = ref(false)
const filters = ref<Record<string, any>>({
  search: '',
  positions: [],
  departments: [],
})
const currentSortString = ref<string | null>(null)
const { itemsPerPage } = useTableItemsPerPage(props.storageKey, 25)

const nextCursor = computed(() => props.nextCursor)
const loadedItemsCount = computed(() => props.items.length)
const totalItemsCount = computed(() => props.total)

const tableLogic = useTableFilters({
  filters,
  filterChoices: computed(() => activeFilterStore.value?.filterChoices || {}) as any,
  searchChoices: computed(() => activeFilterStore.value?.searchChoices || {}) as any,
  filterLoading: activeFilterStore.value?.filterLoading || {},
  fetchFilterChoices: (fieldKey: string) => activeFilterStore.value?.fetchFilterChoices(fieldKey),
  fetchData: async (cursor?: string, limit?: number) => {
    if (!props.reloadKey) return
    await props.fetchPage(appliedSearch.value, cursor, limit, currentSortString.value, filters.value)
  },
  currentSortString,
  itemsPerPage,
  resetFilters: () => {
    filters.value = {
      search: '',
      positions: [],
      departments: [],
    }
  },
  nextCursor,
  loadedItemsCount,
  totalItemsCount,
  sortMap: effectiveSortMap.value,
  filterConfigs: effectiveFilterConfigs.value,
  resetOnUnmount: false,
})

const tableHeaders = computed(() => [
  { title: '', key: 'select', sortable: false, width: 48 },
  ...props.headers,
])

const pageIds = computed(() => props.items.map(item => String(item.id)))
const allSelected = computed(() => pageIds.value.length > 0 && pageIds.value.every(id => selectedIds.value.includes(id)))
const someSelected = computed(() => pageIds.value.some(id => selectedIds.value.includes(id)) && !allSelected.value)

function toggleAll(value: boolean | null) {
  if (value) {
    selectedIds.value = Array.from(new Set([...selectedIds.value, ...pageIds.value]))
    return
  }
  selectedIds.value = selectedIds.value.filter(id => !pageIds.value.includes(id))
}

function toggleOne(id: string, value: boolean | null) {
  if (value)
    selectedIds.value = Array.from(new Set([...selectedIds.value, id]))
  else
    selectedIds.value = selectedIds.value.filter(item => item !== id)
}

function rowProps({ item }: { item: { id: string } }) {
  if (!props.dragEnabled) return {}
  return {
    class: 'staff-row-draggable',
    draggable: true,
    onDragstart: (event: DragEvent) => {
      document.body.classList.add('staff-dnd-grabbing')
      const id = String(item.id)
      const ids = selectedIds.value.includes(id) ? selectedIds.value : [id]
      event.dataTransfer?.setData('application/x-staff-employees', JSON.stringify(ids))
      if (event.dataTransfer)
        event.dataTransfer.effectAllowed = 'move'
    },
    onDragend: () => {
      document.body.classList.remove('staff-dnd-grabbing')
    },
  }
}

onBeforeUnmount(() => {
  document.body.classList.remove('staff-dnd-grabbing')
})

watch(searchDebounced, value => {
  if (ignoreNextSearchFetch.value) {
    ignoreNextSearchFetch.value = false
    return
  }
  appliedSearch.value = value ?? ''
  tableLogic.currentPage.value = 1
  selectedIds.value = []
  tableLogic.triggerServerFilter()
})

watch(() => props.reloadKey, () => {
  tableLogic.currentPage.value = 1
  selectedIds.value = []
  filters.value.positions = []
  filters.value.departments = []
  if (search.value !== '' || searchDebounced.value !== '')
    ignoreNextSearchFetch.value = true
  search.value = ''
  appliedSearch.value = ''
  tableLogic.triggerServerFilter()
})

function onRowClick(_: Event, row: { item: { id: string } }) {
  if (props.rowEditable && row?.item?.id)
    emit('edit', String(row.item.id))
}

onMounted(() => {
  if (props.reloadKey)
    tableLogic.triggerServerFilter()
})
</script>

<template>
  <div class="staff-settings-employee-panel d-flex flex-column ga-3 flex-grow-1 overflow-hidden">
    <div class="d-flex ga-3" :class="xs ? 'flex-column align-stretch' : 'align-center'">
      <VTextField
        :model-value="search"
        placeholder="Найти..."
        prepend-inner-icon="ri-search-line"
        variant="underlined"
        density="compact"
        hide-details
        clearable
        class="staff-settings-search search-underlined"
        :class="{ 'w-100': xs }"
        @update:model-value="search = $event ?? ''"
      />
      <div class="d-flex align-center ga-3 ms-auto">
        <VTooltip
          location="top"
          :text="moveTooltip || ''"
          :disabled="!moveTooltip"
        >
          <template #activator="{ props: tooltipProps }">
            <span v-bind="tooltipProps" class="d-inline-flex">
              <VBtn
                variant="outlined"
                color="secondary"
                class="bg-surface"
                prepend-icon="ri-drag-move-2-line"
                :disabled="!selectedIds.length || !reloadKey"
                @click="emit('move')"
              >
                {{ moveLabel }}
              </VBtn>
            </span>
          </template>
        </VTooltip>
        <VBtn
          v-if="canAdd !== false"
          variant="outlined"
          color="secondary"
          class="bg-surface"
          prepend-icon="ri-user-add-line"
          :disabled="!reloadKey"
          @click="emit('add')"
        >
          {{ addLabel }}
        </VBtn>
      </div>
    </div>

    <div v-if="!reloadKey" class="text-medium-emphasis text-body-2 pa-4">
      {{ emptyText }}
    </div>

    <!-- Активные фильтры -->
    <div
      v-if="reloadKey && tableLogic.activeFilterChips.value.length"
      class="filter-chip d-flex align-center gap-2 py-1 px-1 flex-wrap"
    >
      <VChip
        v-for="chip in tableLogic.activeFilterChips.value"
        :key="chip.key || chip.id || `${chip.config?.key || chip.type}-${chip.value ?? 'no-value'}`"
        size="small"
        closable
        @click:close="tableLogic.removeFilter(chip)"
      >
        {{ chip.label }}
      </VChip>
      <VBtn
        size="x-small"
        variant="text"
        color="secondary"
        prepend-icon="ri-delete-bin-line"
        @click="tableLogic.renewDataAndResetFilters()"
      >
        Сбросить фильтры
      </VBtn>
    </div>

    <VDataTableServer
      v-else
      v-model:items-per-page="itemsPerPage"
      :headers="tableHeaders"
      :items="items"
      :items-length="total"
      item-value="id"
      class="custom-data-table staff-settings-employee-table"
      fixed-header
      :items-per-page-options="[25, 50, 100]"
      :row-props="rowProps"
      @click:row="onRowClick"
      @update:options="tableLogic.handlePageChange"
    >
      <!-- Заголовки с фильтрами -->
      <template
        v-for="col in tableHeaders.filter((h: any) => h.filterType)"
        :key="col.key"
        #[`header.${col.key}`]="{ column }"
      >
        <div class="header-content d-flex align-center">
          <span>{{ column.title }}</span>

          <VIcon
            v-if="col.sortable"
            size="16"
            class="ml-1 custom-sort-icon"
            :icon="tableLogic.getSortIcon(col.key)"
            :class="{
              'is-active': tableLogic.isSortActive(col.key),
              'is-desc': tableLogic.isSortDesc(col.key),
            }"
          />

          <!-- Спиннер загрузки фильтров -->
          <div
            v-if="tableLogic.filterLoading?.[col.filterKey!]"
            class="d-flex align-center justify-center ml-1"
            style="width: 28px; height: 28px;"
          >
            <VProgressCircular
              indeterminate
              color="primary"
              size="20"
              width="2"
            />
          </div>

          <!-- Иконка фильтра -->
          <VIconBtn
            v-else
            variant="plain"
            size="small"
            class="filter-icon-btn"
            :icon="tableLogic.getFilterIcon(col.filterKey!)"
            @click.stop="tableLogic.handleFilterClick(col.filterKey!)"
          />

          <!-- UniversalFilterTree меню -->
          <VMenu
            v-if="col.filterType === 'tree'"
            v-model="tableLogic.filterMenus.value[col.filterKey!]"
            :close-on-content-click="false"
            location="bottom start"
            transition="scale-transition"
          >
            <template #activator="{ props: menuProps }">
              <div v-bind="menuProps" />
            </template>
            <UniversalFilterTree
              :field-key="col.filterKey!"
              :store="activeFilterStore"
              :model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] || []"
              :placeholder="`Поиск: ${column.title}`"
              @update:model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] = $event"
              @apply="tableLogic.applyFilter(col.filterKey!)"
            />
          </VMenu>
        </div>
      </template>

      <!-- Заголовки с сортировкой (без filterType) -->
      <template
        v-for="col in tableHeaders.filter((h: any) => h.sortable && !h.filterType)"
        :key="`sort-${col.key}`"
        #[`header.${col.key}`]="{ column }"
      >
        <div class="header-content d-flex align-center">
          <span>{{ column.title }}</span>
          <VIcon
            size="16"
            class="ml-1 custom-sort-icon"
            :icon="tableLogic.getSortIcon(col.key)"
            :class="{
              'is-active': tableLogic.isSortActive(col.key),
              'is-desc': tableLogic.isSortDesc(col.key),
            }"
          />
        </div>
      </template>
      <template #header.select>
        <div class="d-flex align-center">
          <VCheckboxBtn
            :model-value="allSelected"
            :indeterminate="someSelected"
            @update:model-value="toggleAll"
          />
        </div>
      </template>
      <template #item.select="{ item }">
        <div class="d-flex align-center">
          <VCheckboxBtn
            :model-value="selectedIds.includes(String(item.id))"
            @mousedown.stop
            @click.stop
            @update:model-value="toggleOne(String(item.id), $event)"
          />
        </div>
      </template>
      <template #item.fio="{ item }">
        <span class="name-cell">
          <SearchHighlightText :text="getFullName(item)" :query="appliedSearch" />
        </span>
      </template>
      <template #item.position_name="{ item }">
        <SearchHighlightText :text="item.position_name" :query="appliedSearch" />
      </template>
      <template #item.department_name="{ item }">
        <SearchHighlightText :text="item.department_name" :query="appliedSearch" />
      </template>
      <template #item.phone_number="{ item }">
        <SearchHighlightText :text="item.phone_number" :query="appliedSearch" />
      </template>
      <template #item.email="{ item }">
        <SearchHighlightText :text="item.email" :query="appliedSearch" />
      </template>
      <template #no-data>
        <div class="text-center text-medium-emphasis py-4">
          Сотрудники не найдены
        </div>
      </template>
      <template #bottom>
        <div
          class="custom-data-table-footer"
          :class="{ 'hide-next-button': tableLogic.hideNextButton.value }"
        >
          <VDataTableFooter
            show-current-page
            :items-per-page-options="[25, 50, 100]"
          />
        </div>
      </template>
    </VDataTableServer>
  </div>
</template>

<style scoped lang="scss">
:global(body.staff-dnd-grabbing),
:global(body.staff-dnd-grabbing *) {
  cursor: grabbing !important;
}

.staff-settings-employee-panel {
  min-block-size: 0;
}

/*
 * Таблица занимает остаток панели, строки прокручиваются внутри .v-table__wrapper.
 * Глобальный overflow-y: visible !important для .custom-data-table рассчитан на скролл окна,
 * а здесь панель ограничена по высоте карточкой — перебиваем.
 */
.staff-settings-employee-table {
  flex: 1 1 auto;
  min-block-size: 0;

  :deep(.v-table__wrapper) {
    min-block-size: 0;
    overflow-y: auto !important;
  }
}

.staff-settings-search {
  flex: 1 1 12rem;
  min-inline-size: 12rem;
  max-inline-size: 450px;
}

.name-cell {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 15px;
  font-weight: 500;
  line-height: 22px;
}

:deep(.v-data-table-header__sort-icon) {
  display: none !important;
}

.custom-sort-icon {
  font-size: 16px;
  transition: transform 0.3s ease, opacity 0.2s ease, color 0.2s ease;
  opacity: 0.15;
  display: inline-block;
  vertical-align: middle;
  cursor: pointer;

  &.is-active {
    opacity: 1 !important;
    color: rgb(var(--v-theme-primary)) !important;
  }

  &.is-desc {
    transform: rotate(180deg);
  }
}

:deep(.v-data-table__th--sortable:hover) .custom-sort-icon {
  opacity: 0.6;
}

.filter-icon-btn {
  opacity: 0.7;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
}

:deep(.custom-data-table thead th),
:deep(.custom-data-table thead .v-data-table__th) {
  inset-block-start: 0 !important;
}

:deep(.custom-data-table .v-data-table__th) {
  background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  line-height: 22px !important;
  position: relative;
  padding-inline: 1rem;
}

:deep(.custom-data-table .v-data-table__td) {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  padding-inline: 1rem;
}

:deep(.v-data-table__td) {
  padding-block: 0.75rem !important;
}

:deep(.custom-data-table .v-data-table__th:not(:last-child))::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 2px;
  height: 14px;
  background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
}

:deep(.custom-data-table tr):hover {
  box-shadow: 0 2px 4px rgb(var(--v-shadow-key-umbra-color), var(--v-shadow-xs-opacity));
  cursor: pointer;
  background-color: var(--table-row-hover) !important;
}

:deep(.custom-data-table tr.staff-row-draggable),
:deep(.custom-data-table tr.staff-row-draggable):hover {
  cursor: grab;
}

.custom-data-table-footer {
  :deep(.v-data-table-footer) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;

    .v-data-table-footer__items-per-page > span:first-child {
      display: none !important;
    }

    .v-data-table-footer__pagination {
      order: 1;
    }

    .v-data-table-footer__items-per-page {
      order: 2;
    }

    .v-data-table-footer__info {
      order: 3;
    }

    .v-pagination__last > button {
      display: none !important;
    }

    .v-pagination__next > button[aria-disabled='true'] {
      display: none !important;
    }
  }

  &.hide-next-button {
    :deep(.v-pagination__next) {
      display: none !important;
    }
  }
}

:deep(.search-underlined.v-text-field) {
  .v-field__control {
    height: 36px !important;
  }

  .v-field__prepend-inner {
    align-self: center !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

  .v-field__input {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    min-height: 36px !important;
    align-self: center !important;
    display: flex !important;
    align-items: center !important;
  }
}
</style>
